// Merges several independently-paged transaction feeds into one chronological
// list without introducing holes.
//
// Each card is paged on its own, so a card is only known-complete down to the
// oldest row fetched for it. Concatenating raw pages therefore produces a list
// that *looks* continuous but silently omits the busy card's older rows while
// still showing a quiet card's — the merge is only trustworthy down to the
// newest of the per-feed boundaries, so everything below that is withheld
// until the feed that defines the boundary is paged further.

export interface PagedFeed<T> {
  readonly items: readonly T[];
  // False only when the feed is provably exhausted. A feed with a page in
  // flight or a page that failed still counts as having more, so the boundary
  // holds instead of the list briefly claiming rows that aren't there.
  readonly hasMore: boolean;
}

export interface MergeOptions<T> {
  readonly timeOf: (item: T) => number;
  readonly keyOf: (item: T) => string;
}

export interface MergedFeeds<T> {
  // Newest first, de-duplicated, gap-free.
  readonly items: readonly T[];
  // Every feed is fully loaded — the list has reached the genuine oldest row.
  readonly exhausted: boolean;
  // Indices of the feeds pinning the boundary; page these to reveal more.
  readonly limiting: readonly number[];
}

const oldestTime = <T>(feed: PagedFeed<T>, timeOf: (item: T) => number): number => {
  let oldest = Number.POSITIVE_INFINITY;
  for (const item of feed.items) {
    const t = timeOf(item);
    if (Number.isFinite(t) && t < oldest) oldest = t;
  }
  return oldest;
};

export const mergePagedFeeds = <T>(
  feeds: readonly PagedFeed<T>[],
  { timeOf, keyOf }: MergeOptions<T>,
): MergedFeeds<T> => {
  // The boundary is the newest of the unfinished feeds' oldest rows. A feed
  // with more pages but nothing loaded yet contributes +Infinity: it knows
  // nothing about its own timeline, so nothing at all can be shown.
  let boundary = Number.NEGATIVE_INFINITY;
  const limiting: number[] = [];
  feeds.forEach((feed, index) => {
    if (!feed.hasMore) return;
    const oldest = oldestTime(feed, timeOf);
    if (oldest > boundary) {
      boundary = oldest;
      limiting.length = 0;
    }
    if (oldest === boundary) limiting.push(index);
  });

  const seen = new Set<string>();
  const merged: T[] = [];
  for (const feed of feeds) {
    for (const item of feed.items) {
      const time = timeOf(item);
      if (!Number.isFinite(time)) continue;
      if (boundary !== Number.NEGATIVE_INFINITY && time < boundary) continue;
      const key = keyOf(item);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(item);
    }
  }
  merged.sort((a, b) => timeOf(b) - timeOf(a));

  return { items: merged, exhausted: limiting.length === 0, limiting };
};
